import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { devAdminAPI } from '../../../services/adminService';
import MultiMediaDisplay from '../../../components/social/posts/MultiMediaDisplay';
import ModerationActionModal from '../../../components/modals/ModerationActionModal';
import './PostModerationManager.css';

const PostModerationManager = ({ mode = 'all' }) => {
  const { t } = useTranslation();

  const [posts, setPosts] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedPost, setSelectedPost] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, post: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteReasons, setDeleteReasons] = useState([]);
  const [otherReasonText, setOtherReasonText] = useState('');
  const [deleteImages, setDeleteImages] = useState([]);
  const [deleteImagePreviews, setDeleteImagePreviews] = useState([]);
  const deleteFileInputRef = useRef(null);

  const [actionModal, setActionModal] = useState({ isOpen: false });
  const [isActionProcessing, setIsActionProcessing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [countryFilter, setCountryFilter] = useState('');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // Load posts
  const loadPosts = useCallback(async (page = 1) => {
    setIsLoadingPosts(true);
    setApiError(null);
    try {
      const params = {
        page,
        ...(mode === 'reported' ? { reported: 1 } : {}),
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(countryFilter ? { country: countryFilter } : {}),
      };
      const response = await devAdminAPI.getPosts(params);
      const data = response?.data;
      const postList = data?.data?.data || data?.data || [];
      const paginationData = data?.data?.pagination || data?.pagination || { current_page: 1, last_page: 1, total: 0 };
      setPosts(postList);
      setPagination(paginationData);
      setAvailableCountries(data?.data?.available_countries || []);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load posts:', error);
      setApiError(t('adminDashboard.postModeration.loadError', 'Failed to load posts.'));
    } finally {
      setIsLoadingPosts(false);
    }
  }, [mode, searchQuery, countryFilter, t]);

  // Load posts on mount and when mode/search/country changes
  useEffect(() => {
    setCurrentPage(1);
    loadPosts(1);
  }, [loadPosts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadPosts(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, loadPosts]);

  const handleSelectPost = async (post) => {
    setSelectedPost(post);
    setDeleteError(null);
    setIsLoadingDetails(true);

    try {
      const response = await devAdminAPI.getPostDetails(post.id);
      const data = response?.data?.data || response?.data;
      setSelectedPost(prev => ({
        ...prev,
        ...data,
        id: data?.id || prev?.id,
      }));
    } catch (error) {
      console.error('Failed to load post details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleDeleteCheck = (post) => {
    setDeleteModal({ isOpen: true, post });
    setDeleteError(null);
    setDeleteReasons([]);
    setOtherReasonText('');
  };

  const REPORT_REASONS = [
    { value: 'spam', label: t('reportReasons.spam', 'Spam') },
    { value: 'inappropriate', label: t('reportReasons.inappropriate', 'Inappropriate Content') },
    { value: 'harassment', label: t('reportReasons.harassment', 'Harassment or Bullying') },
    { value: 'fake_info', label: t('reportReasons.fake_info', 'Fake Information') },
    { value: 'violence', label: t('reportReasons.violence', 'Violence or Threats') },
    { value: 'copyright', label: t('reportReasons.copyright', 'Copyright Violation') },
    { value: 'animal_cruelty', label: t('reportReasons.animal_cruelty', 'Animal Cruelty') },
    { value: 'other', label: t('reportReasons.other', 'Other') },
  ];

  const toggleReason = (value) => {
    setDeleteReasons(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
  };

  const handleDeleteImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const combined = [...deleteImages, ...files].slice(0, 5);
    setDeleteImages(combined);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setDeleteImagePreviews(prev => [...prev, ...newPreviews].slice(0, 5));
  };

  const removeDeleteImage = (index) => {
    setDeleteImages(prev => prev.filter((_, i) => i !== index));
    setDeleteImagePreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleConfirmDelete = async () => {
    const post = deleteModal.post;
    if (!post || isDeleting) return;

    setIsDeleting(true);
    try {
      const reasons = deleteReasons.length > 0 ? deleteReasons : undefined;
      const customReason = otherReasonText.trim() || undefined;
      await devAdminAPI.deletePost(post.id, { reasons, reason: customReason, images: deleteImages });
      setDeleteModal({ isOpen: false, post: null });
      setSelectedPost(null);
      setDeleteReasons([]);
      setOtherReasonText('');
      deleteImagePreviews.forEach(url => URL.revokeObjectURL(url));
      setDeleteImagePreviews([]);
      setDeleteImages([]);
      loadPosts(currentPage);
    } catch (error) {
      console.error('Post delete failed:', error);
      setDeleteError(error?.response?.data?.message || t('adminDashboard.postModeration.deleteFailed', 'Deletion failed.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredCountries = availableCountries.filter(c => {
    if (!countrySearch) return true;
    return c.name?.toLowerCase().includes(countrySearch.toLowerCase());
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getUserName = (user) => {
    if (!user) return 'Unknown';
    const first = typeof user.first_name === 'string' ? user.first_name : '';
    const last = typeof user.last_name === 'string' ? user.last_name : '';
    if (first && last) return `${first} ${last}`;
    if (first) return first;
    return 'Unknown';
  };

  const getInitial = (user) => {
    const name = getUserName(user);
    return name.charAt(0).toUpperCase();
  };

  const handleAction = async (actionKey) => {
    if (!selectedPost || isActionProcessing) return;
    setIsActionProcessing(true);

    try {
      switch (actionKey) {
        case 'delete':
          handleDeleteCheck(selectedPost);
          setActionModal({ isOpen: false });
          setIsActionProcessing(false);
          return;
        case 'ignore':
          await devAdminAPI.ignorePostReports(selectedPost.id);
          break;
        default:
          break;
      }
      setActionModal({ isOpen: false });
      loadPosts(currentPage);
    } catch (error) {
      console.error('Action failed:', error);
      setDeleteError(error?.response?.data?.message || 'Action failed. Please try again.');
    } finally {
      setIsActionProcessing(false);
    }
  };

  const postActions = [
    {
      key: 'delete',
      label: t('moderation.actions.deletePost', 'Delete Post'),
      description: t('moderation.actions.deletePostDesc', 'Permanently remove this post'),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
        </svg>
      ),
      variant: 'danger',
      confirmTitle: t('moderation.actions.deletePostConfirmTitle', 'Delete Post'),
      confirmMessage: t('moderation.actions.deletePostConfirmMessage', 'This will permanently delete the post and notify the author.'),
      confirmButton: t('moderation.actions.deletePost', 'Delete'),
    },
    {
      key: 'ignore',
      label: t('moderation.actions.ignore', 'Ignore Reports'),
      description: t('moderation.actions.ignorePostDesc', 'Mark reports as ignored'),
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
        </svg>
      ),
      confirmTitle: t('moderation.actions.ignoreConfirmTitle', 'Ignore Reports'),
      confirmMessage: t('moderation.actions.ignorePostConfirmMessage', 'All pending reports for this post will be marked as ignored.'),
      confirmButton: t('moderation.actions.ignore', 'Ignore'),
    },
  ];

  return (
    <div className="post-moderation">
      <div className="post-moderation-layout">
        {/* Left Panel — Post List */}
        <div className="post-moderation-panel post-moderation-list">
          <div className="post-moderation-panel-header">
            <h3 className="post-moderation-panel-title">
              {mode === 'reported'
                ? t('adminDashboard.postModeration.reportedPosts', 'Reported Posts')
                : t('adminDashboard.postModeration.allPosts', 'All Posts')}
            </h3>
            <span className="post-moderation-count">{pagination.total}</span>
          </div>

          <div className="user-manager-filters">
            <div className="post-moderation-search" style={{ borderBottom: 'none', padding: 0, background: 'transparent' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder={t('adminDashboard.postModeration.searchPlaceholder', 'Search posts or authors...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="post-moderation-search-clear"
                  onClick={() => setSearchQuery('')}
                  title={t('common.clear', 'Clear')}
                >
                  ×
                </button>
              )}
            </div>
            {/* Country Filter */}
            <div className="user-manager-country-filter">
              <button
                className="user-manager-country-filter-toggle"
                onClick={() => setCountryDropdownOpen(o => !o)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {countryFilter
                    ? availableCountries.find(c => String(c.id) === String(countryFilter))?.name || t('adminDashboard.userManagement.country', 'Country')
                    : t('adminDashboard.userManagement.allCountries', 'All Countries')}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: countryDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="user-manager-country-filter-dropdown">
                  <div className="user-manager-country-filter-search">
                    <input
                      type="text"
                      placeholder={t('adminDashboard.userManagement.searchCountry', 'Search country...')}
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="user-manager-country-filter-list">
                    <button
                      className={`user-manager-country-filter-item ${!countryFilter ? 'active' : ''}`}
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.allCountries', 'All Countries')}
                    </button>
                    {filteredCountries.map(country => (
                      <button
                        key={country.id}
                        className={`user-manager-country-filter-item ${String(countryFilter) === String(country.id) ? 'active' : ''}`}
                        onClick={() => { setCountryFilter(String(country.id)); setCountryDropdownOpen(false); setCountrySearch(''); }}
                      >
                        {country.name}
                      </button>
                    ))}
                  </div>
                  {countryFilter && (
                    <button
                      className="user-manager-country-filter-clear"
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.clearFilter', 'Clear filter')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="post-moderation-list-content">
            {isLoadingPosts ? (
              <div className="post-moderation-loading">{t('adminDashboard.postModeration.loading', 'Loading posts...')}</div>
            ) : apiError ? (
              <div className="post-moderation-error">
                <p>{apiError}</p>
                <button onClick={() => loadPosts(currentPage)}>{t('common.retry', 'Retry')}</button>
              </div>
            ) : posts.length === 0 ? (
              <div className="post-moderation-empty">
                <p>{t('adminDashboard.postModeration.noPosts', 'No posts found.')}</p>
              </div>
            ) : (
              <>
                {posts.map(post => (
                  <div
                    key={post.id}
                    className={`post-moderation-list-item ${selectedPost?.id === post.id ? 'selected' : ''}`}
                    onClick={() => handleSelectPost(post)}
                  >
                    <div className="post-moderation-list-item-main">
                      {/* Author avatar as main left image */}
                      {post.user?.profile_image_url ? (
                        <img src={post.user.profile_image_url} alt="" className="post-moderation-list-avatar" />
                      ) : (
                        <div className="post-moderation-list-avatar post-moderation-list-avatar-placeholder">
                          {getInitial(post.user)}
                        </div>
                      )}
                      <div className="post-moderation-list-item-info">
                        <div className="post-moderation-list-item-top">
                          <span className="post-moderation-list-item-author-name">{getUserName(post.user)}</span>
                          <span className="post-moderation-list-item-date">{formatDate(post.created_at)}</span>
                        </div>
                        <p className="post-moderation-list-item-preview">{stripHtml(post.content) || '—'}</p>
                        {/* Post media thumbnail inline */}
                        {post.media_preview && (
                          <div className="post-moderation-list-item-media">
                            <img src={post.media_preview} alt="" />
                          </div>
                        )}
                      </div>
                    </div>
                    {post.reports_count > 0 && (
                      <div className="post-moderation-report-badge">
                        <span className="post-moderation-report-dot"></span>
                        {post.reports_count}
                      </div>
                    )}
                  </div>
                ))}

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="post-moderation-pagination">
                    <button
                      disabled={currentPage <= 1}
                      onClick={() => loadPosts(currentPage - 1)}
                    >
                      {t('common.prev', 'Prev')}
                    </button>
                    <span>{currentPage} / {pagination.last_page}</span>
                    <button
                      disabled={currentPage >= pagination.last_page}
                      onClick={() => loadPosts(currentPage + 1)}
                    >
                      {t('common.next', 'Next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Panel — Post Details */}
        <div className="post-moderation-panel post-moderation-details">
          {!selectedPost ? (
            <div className="post-moderation-empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>{t('adminDashboard.postModeration.selectPost', 'Select a post to view details and manage it.')}</p>
            </div>
          ) : (
            <>
              <div className="post-moderation-details-header">
                <div className="post-moderation-details-header-main">
                  {selectedPost.user?.profile_image_url ? (
                    <img
                      src={selectedPost.user.profile_image_url}
                      alt={getUserName(selectedPost.user)}
                      className="post-moderation-details-avatar"
                    />
                  ) : (
                    <div className="post-moderation-details-avatar post-moderation-details-avatar-placeholder">
                      {getInitial(selectedPost.user)}
                    </div>
                  )}
                  <div>
                    <h2>{getUserName(selectedPost.user)}</h2>
                    <p className="post-moderation-details-subtitle">
                      {formatDate(selectedPost.created_at)}
                      {selectedPost.reports?.length > 0 && (
                        <span className="post-moderation-details-report-count">
                          {' • '}
                          {selectedPost.reports.length} {t('adminDashboard.postModeration.reports', 'report(s)')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  className="post-moderation-actions-btn"
                  onClick={() => setActionModal({ isOpen: true })}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="1"></circle>
                    <circle cx="19" cy="12" r="1"></circle>
                    <circle cx="5" cy="12" r="1"></circle>
                  </svg>
                  {t('adminDashboard.postModeration.actions', 'Actions')}
                </button>
              </div>

              <div className="post-moderation-details-content">
                {isLoadingDetails ? (
                  <div className="post-moderation-loading">{t('adminDashboard.postModeration.loadingDetails', 'Loading details...')}</div>
                ) : (
                  <>
                    {/* Stats Cards */}
                    <div className="post-moderation-stats-grid">
                      <div className="post-moderation-stat-card">
                        <span className="post-moderation-stat-label">{t('adminDashboard.postModeration.reactions', 'Reactions')}</span>
                        <span className="post-moderation-stat-value">{selectedPost.reactions_count ?? 0}</span>
                      </div>
                      <div className="post-moderation-stat-card">
                        <span className="post-moderation-stat-label">{t('adminDashboard.postModeration.comments', 'Comments')}</span>
                        <span className="post-moderation-stat-value">{selectedPost.comments_count ?? 0}</span>
                      </div>
                      <div className="post-moderation-stat-card">
                        <span className="post-moderation-stat-label">{t('adminDashboard.postModeration.rating', 'Rating')}</span>
                        <span className="post-moderation-stat-value">
                          {selectedPost.average_rating !== null && selectedPost.average_rating !== undefined
                            ? Number(selectedPost.average_rating).toFixed(1)
                            : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Two-column layout: Content+Media (left) | Comments (right) */}
                    <div className="post-moderation-details-columns">
                      {/* Left column */}
                      <div className="post-moderation-details-left">
                        {/* Post Content */}
                        <div className="post-moderation-section">
                          <h3>{t('adminDashboard.postModeration.content', 'Content')}</h3>
                          {selectedPost.content ? (
                            <div
                              className="post-moderation-content-text"
                              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                            />
                          ) : (
                            <p className="post-moderation-content-text">—</p>
                          )}
                        </div>

                        {/* Media Gallery */}
                        {selectedPost.media && selectedPost.media.length > 0 && (
                          <div className="post-moderation-section">
                            <h3>{t('adminDashboard.postModeration.media', 'Media')}</h3>
                            <div className="post-moderation-media-wrapper">
                              <MultiMediaDisplay
                                media={selectedPost.media.map(m => ({
                                  url: m.url,
                                  type: m.file_type,
                                  file_type: m.file_type,
                                }))}
                                slideHeight={200}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right column — Comments */}
                      <div className="post-moderation-details-right">
                        {selectedPost.comments && selectedPost.comments.length > 0 ? (
                          <div className="post-moderation-section">
                            <h3>{t('adminDashboard.postModeration.commentsSection', 'Comments')} ({selectedPost.comments.length})</h3>
                            <div className="post-moderation-comments-scroll">
                              {selectedPost.comments.map(comment => (
                                <div key={comment.id} className="post-moderation-comment-card">
                                  <div className="post-moderation-comment-header">
                                    {comment.user?.profile_image_url ? (
                                      <img src={comment.user.profile_image_url} alt="" className="post-moderation-comment-avatar" />
                                    ) : (
                                      <div className="post-moderation-comment-avatar-placeholder">
                                        {(comment.user?.name || 'U').charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div className="post-moderation-comment-meta">
                                      <span className="post-moderation-comment-author">{comment.user?.name || 'Unknown'}</span>
                                      <span className="post-moderation-comment-date">{formatDate(comment.created_at)}</span>
                                    </div>
                                  </div>
                                  <div className="post-moderation-comment-text">{comment.text || '—'}</div>

                                  {/* Replies */}
                                  {comment.replies && comment.replies.length > 0 && (
                                    <div className="post-moderation-replies-list">
                                      {comment.replies.map(reply => (
                                        <div key={reply.id} className="post-moderation-reply-card">
                                          <div className="post-moderation-comment-header">
                                            {reply.user?.profile_image_url ? (
                                              <img src={reply.user.profile_image_url} alt="" className="post-moderation-comment-avatar" />
                                            ) : (
                                              <div className="post-moderation-comment-avatar-placeholder">
                                                {(reply.user?.name || 'U').charAt(0).toUpperCase()}
                                              </div>
                                            )}
                                            <div className="post-moderation-comment-meta">
                                              <span className="post-moderation-comment-author">{reply.user?.name || 'Unknown'}</span>
                                              <span className="post-moderation-comment-date">{formatDate(reply.created_at)}</span>
                                            </div>
                                          </div>
                                          <div className="post-moderation-comment-text">{reply.text || '—'}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="post-moderation-section">
                            <h3>{t('adminDashboard.postModeration.commentsSection', 'Comments')}</h3>
                            <p className="post-moderation-empty-text">{t('adminDashboard.postModeration.noComments', 'No comments yet.')}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Reports */}
                    {selectedPost.reports && selectedPost.reports.length > 0 && (
                      <div className="post-moderation-section">
                        <h3>{t('adminDashboard.postModeration.reportsSection', 'Reports')}</h3>
                        <div className="post-moderation-reports-list">
                          {selectedPost.reports.map(report => (
                            <div key={report.id} className="post-moderation-report-card">
                              <div className="post-moderation-report-header">
                                <span className="post-moderation-report-reporter">
                                  {report.reporter?.name || t('adminDashboard.postModeration.unknownReporter', 'Unknown')}
                                </span>
                                <span className="post-moderation-report-date">
                                  {formatDate(report.created_at)}
                                </span>
                              </div>
                              <div className="post-moderation-report-reason">
                                <strong>{t('adminDashboard.postModeration.reason', 'Reason')}:</strong> {report.reason || '—'}
                              </div>
                              {report.description && (
                                <div className="post-moderation-report-description">
                                  {report.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Modal with Reason */}
      {deleteModal.isOpen && (
        <div className="post-moderation-delete-overlay" onClick={() => !isDeleting && setDeleteModal({ isOpen: false, post: null })}>
          <div className="post-moderation-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="post-moderation-delete-modal-header">
              <div className="post-moderation-delete-modal-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <div>
                <h3>{t('adminDashboard.postModeration.confirmDeleteTitle', 'Delete Post')}</h3>
                <p>{t('adminDashboard.postModeration.confirmDeleteMessage', 'This action is permanent and cannot be undone. The author will be notified.')}</p>
              </div>
            </div>

            <div className="post-moderation-delete-reason">
              <label>{t('adminDashboard.postModeration.deleteReasonLabel', 'Select reason(s) for deletion')}</label>
              <div className="post-moderation-delete-chips">
                {REPORT_REASONS.map(({ value, label }) => {
                  const selected = deleteReasons.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      className={`post-moderation-delete-chip ${selected ? 'selected' : ''}`}
                      onClick={() => toggleReason(value)}
                      disabled={isDeleting}
                    >
                      {selected && (
                        <svg className="post-moderation-delete-chip-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>

              <div className="post-moderation-delete-other-wrap open">
                <textarea
                  rows={2}
                  value={otherReasonText}
                  onChange={(e) => setOtherReasonText(e.target.value)}
                  placeholder={t('adminDashboard.postModeration.otherReasonPlaceholder', 'Please specify the reason...')}
                  disabled={isDeleting}
                />
              </div>

              {/* Image Upload */}
              <div className="post-moderation-delete-images">
                <label className="post-moderation-delete-section-label">
                  {t('moderation.evidenceImages', 'Evidence Images')}
                  <span className="post-moderation-delete-section-optional">{t('common.optional', 'Optional')} • {deleteImages.length}/5</span>
                </label>
                <div className="post-moderation-delete-image-grid">
                  {deleteImagePreviews.map((preview, index) => (
                    <div key={index} className="post-moderation-delete-image-thumb">
                      <img src={preview} alt="" />
                      <button
                        type="button"
                        className="post-moderation-delete-image-remove"
                        onClick={() => removeDeleteImage(index)}
                        disabled={isDeleting}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {deleteImages.length < 5 && (
                    <button
                      type="button"
                      className="post-moderation-delete-image-add"
                      onClick={() => deleteFileInputRef.current?.click()}
                      disabled={isDeleting}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  ref={deleteFileInputRef}
                  onChange={handleDeleteImageSelect}
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                />
              </div>

              {deleteReasons.length > 0 && (
                <div className="post-moderation-delete-selection-count">
                  {deleteReasons.length} {t('adminDashboard.postModeration.reasonsSelected', 'reason(s) selected')}
                </div>
              )}
            </div>

            {deleteError && (
              <div className="post-moderation-delete-modal-error">{deleteError}</div>
            )}

            <div className="post-moderation-delete-actions">
              <button
                className="post-moderation-delete-cancel"
                onClick={() => setDeleteModal({ isOpen: false, post: null })}
                disabled={isDeleting}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="post-moderation-delete-confirm"
                onClick={handleConfirmDelete}
                disabled={isDeleting || deleteReasons.length === 0}
              >
                {isDeleting ? t('common.deleting', 'Deleting...') : t('adminDashboard.postModeration.deleteConfirm', 'Delete Post')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Moderation Actions Modal */}
      <ModerationActionModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal({ isOpen: false })}
        title={t('moderation.actions.title', 'Take Action')}
        actions={postActions}
        onAction={handleAction}
        isProcessing={isActionProcessing}
      />

      {/* Delete Error Toast */}
      {deleteError && (
        <div className="post-moderation-delete-error" style={{ zIndex: 12000 }}>
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default PostModerationManager;
